import { useCallback, useEffect, useState } from "react";
import { Play, RefreshCw, Trash2 } from "lucide-react";
import { formatDateTime } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOICE_STATUS_LABELS = { nova: "Nový", vyrizeno: "Vybavený" };

export const AdminVoice = ({ token, logout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [audioUrls, setAudioUrls] = useState({});
  const [loadingAudio, setLoadingAudio] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const res = await fetch(`${API}/admin/voice-messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }
      const data = await res.json();
      setItems(data.items || []);
    } catch {
      setListError("Vzkazy se nepodařilo načíst. Zkontrolujte připojení.");
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    load();
  }, [load]);

  const play = async (id) => {
    if (audioUrls[id]) return;
    setLoadingAudio(id);
    try {
      const res = await fetch(`${API}/admin/voice-messages/${id}/audio`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) return logout();
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      setAudioUrls((prev) => ({ ...prev, [id]: URL.createObjectURL(blob) }));
    } catch {
      setListError("Nahrávku se nepodařilo načíst.");
    } finally {
      setLoadingAudio("");
    }
  };

  const changeStatus = async (id, status) => {
    const prev = items;
    setItems(items.map((i) => (i.id === id ? { ...i, status } : i)));
    try {
      const res = await fetch(`${API}/admin/voice-messages/${id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (res.status === 401 || res.status === 403) logout();
      else if (!res.ok) setItems(prev);
    } catch {
      setItems(prev);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Opravdu smazat tento hlasový vzkaz? Nahrávka se smaže trvale.")) return;
    try {
      const res = await fetch(`${API}/admin/voice-messages/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) return logout();
      if (res.ok) setItems(items.filter((i) => i.id !== id));
    } catch {
      setListError("Vzkaz se nepodařilo smazat.");
    }
  };

  return (
    <div data-testid="admin-tab-voice">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-display text-2xl md:text-3xl text-[#252525]">Hlasové vzkazy</h2>
        <button
          type="button"
          onClick={load}
          data-testid="voice-refresh-button"
          className="inline-flex items-center gap-2 border border-[#E5E5E5] bg-white hover:border-[#B69A58] transition-colors duration-300 px-5 py-3 text-xs uppercase tracking-widest font-medium text-[#252525]"
        >
          <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
          Obnovit
        </button>
      </div>

      <p className="text-sm text-[#737373] mt-4" data-testid="voice-count">
        {loading ? "Načítám…" : `${items.length} ${items.length === 1 ? "vzkaz" : items.length >= 2 && items.length <= 4 ? "vzkazy" : "vzkazů"}`}
      </p>

      {listError && <p role="alert" data-testid="voice-list-error" className="text-sm text-red-700 mt-6">{listError}</p>}

      {!loading && items.length === 0 && !listError && (
        <div className="mt-8 border border-[#F0F0F0] bg-white p-10 text-center" data-testid="voice-empty-list">
          <p className="text-base text-[#737373]">Zatím žádné hlasové vzkazy.</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-8 space-y-4">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`voice-row-${item.id}`}
              className="border border-[#F0F0F0] bg-white p-6 flex flex-wrap items-center gap-x-8 gap-y-4"
            >
              <div className="min-w-[140px]">
                <p className="text-xs text-[#737373]">{formatDateTime(item.created_at)}</p>
                <p className="text-xs text-[#A3A3A3] mt-1">{Math.max(1, Math.round(item.size_bytes / 1024))} kB</p>
              </div>
              <div className="min-w-[180px]">
                <p className="font-medium text-[#252525]">{item.name}</p>
                <p className="text-sm text-[#4A4A4A]">{item.email}</p>
              </div>
              <div className="flex-1 min-w-[220px]">
                {audioUrls[item.id] ? (
                  <audio controls src={audioUrls[item.id]} className="w-full max-w-xs" data-testid={`voice-audio-${item.id}`} />
                ) : (
                  <button
                    type="button"
                    onClick={() => play(item.id)}
                    data-testid={`voice-play-${item.id}`}
                    disabled={loadingAudio === item.id}
                    className="inline-flex items-center gap-2 border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-5 py-2.5 text-xs uppercase tracking-widest font-medium disabled:opacity-50"
                  >
                    <Play className="h-4 w-4" strokeWidth={1.5} />
                    {loadingAudio === item.id ? "Načítám…" : "Přehrát"}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <select
                  data-testid={`voice-status-${item.id}`}
                  value={item.status}
                  onChange={(e) => changeStatus(item.id, e.target.value)}
                  className="bg-white border border-[#E5E5E5] focus:border-[#B69A58] outline-none py-2 px-2 text-sm rounded-none"
                  aria-label={`Změnit stav vzkazu od ${item.name}`}
                >
                  <option value="nova">{VOICE_STATUS_LABELS.nova}</option>
                  <option value="vyrizeno">{VOICE_STATUS_LABELS.vyrizeno}</option>
                </select>
                <button
                  type="button"
                  onClick={() => remove(item.id)}
                  data-testid={`voice-delete-${item.id}`}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-[#737373] hover:text-red-700 transition-colors duration-300"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  Smazat
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
